import React, { useState } from 'react';
import { useSecretGesture, verifyAdminPassphrase } from './adminUnlock';

const groupDigits = (digits) => digits.replace(/\B(?=(\d{3})+(?!\d))/g, ',');

// Keep only digits and a single decimal point
const cleanDecimal = (raw) => {
  let s = String(raw).replace(/[^\d.]/g, '');
  const firstDot = s.indexOf('.');
  if (firstDot !== -1) {
    s = s.slice(0, firstDot + 1) + s.slice(firstDot + 1).replace(/\./g, '');
  }
  return s;
};

// Turn stored text into the display string, e.g. "5554555" -> "5,554,555"
// or (decimal) "1228.79" -> "1,228.79"
const formatValue = (raw, prefix, decimal) => {
  if (decimal) {
    const s = cleanDecimal(raw);
    if (!s || s === '.') return `${prefix}0`;
    const [intRaw, decPart] = s.split('.');
    const intPart = (intRaw || '0').replace(/^0+(?=\d)/, '') || '0';
    const grouped = groupDigits(intPart);
    return decPart !== undefined ? `${prefix}${grouped}.${decPart}` : `${prefix}${grouped}`;
  }
  const digits = String(raw).replace(/\D/g, '');
  if (!digits) return `${prefix}0`;
  const trimmed = digits.replace(/^0+(?=\d)/, '');
  return `${prefix}${groupDigits(trimmed)}`;
};

const AdminEditableValue = ({ className, prefix = '', value, onCommit, decimal = false }) => {
  const [phase, setPhase] = useState('idle'); // 'idle' | 'prompt' | 'editing'
  const [passphrase, setPassphrase] = useState('');
  const [draft, setDraft] = useState('');
  const [error, setError] = useState('');
  const [checking, setChecking] = useState(false);

  const gestureHandlers = useSecretGesture(() => {
    setPassphrase('');
    setError('');
    setPhase('prompt');
  }, phase === 'idle');

  const verifyPassphrase = async () => {
    setChecking(true);
    setError('');
    try {
      if (await verifyAdminPassphrase(passphrase)) {
        setDraft(String(value ?? ''));
        setPhase('editing');
      } else {
        setError('Incorrect');
      }
    } catch (e) {
      setError('Network error');
    } finally {
      setChecking(false);
    }
  };

  const commitDraft = () => {
    if (decimal) {
      const s = cleanDecimal(draft);
      onCommit(s && s !== '.' ? s : '0');
    } else {
      const digits = String(draft).replace(/\D/g, '');
      onCommit(digits ? String(Number(digits)) : '0');
    }
    setPhase('idle');
  };

  if (phase === 'prompt') {
    return (
      <span className="admin-edit-box">
        <input
          type="password"
          className="admin-edit-input"
          placeholder="Password"
          value={passphrase}
          autoFocus
          onChange={(e) => setPassphrase(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') verifyPassphrase();
            if (e.key === 'Escape') setPhase('idle');
          }}
        />
        <button type="button" className="admin-edit-btn" onClick={verifyPassphrase} disabled={checking}>
          {checking ? '…' : 'OK'}
        </button>
        {error && <span className="admin-edit-error">{error}</span>}
      </span>
    );
  }

  if (phase === 'editing') {
    return (
      <span className="admin-edit-box">
        <input
          type="text"
          inputMode="numeric"
          className="admin-edit-input"
          value={draft}
          autoFocus
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') commitDraft();
            if (e.key === 'Escape') setPhase('idle');
          }}
        />
        <button type="button" className="admin-edit-btn" onClick={commitDraft}>Save</button>
      </span>
    );
  }

  return (
    <span className={className} {...gestureHandlers}>
      {formatValue(value, prefix, decimal)}
    </span>
  );
};

export default AdminEditableValue;
